import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useFamily } from '../context/FamilyContext.jsx';
import { useDeleteAccount } from '../context/DeleteAccountContext.jsx';
import useAnalytics from '../../hooks/useAnalytics.js';
import Button from '../ui/Button.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card.jsx';
import HouseholdProfileForm, { PROFILE_FORM_SECTIONS } from '../components/HouseholdProfileForm.jsx';
import { Sheet } from '../ui/Sheet.jsx';
import Onboarding from '../components/Onboarding.jsx';
import LoggedOutLandingPage from '../components/LoggedOutLandingPage.jsx';
import ProfileSetupFlow from '../components/ProfileSetupFlow.jsx';
import ProfileInfoSectionCard from '../components/ProfileInfoSectionCard.jsx';
import QuestionnaireInfoStep, { QUESTIONNAIRE_INFO_STEP_IDS } from '../components/QuestionnaireInfoStep.jsx';
import { useEmbeddedNavigation } from '../hooks/useEmbeddedMode.js';
import useOnboardingStatus from '../hooks/useOnboardingStatus.js';
import { formatDecimalHoursCompact } from '../utils/profileForm.js';
import { scrollElementToTop } from '../utils/scroll.js';

function DeleteAccountLink() {
  const { open } = useDeleteAccount();
  return (
    <div className="pt-2 pb-6 text-center">
      <button
        type="button"
        onClick={open}
        className="text-sm font-medium text-red-600 dark:text-red-400 underline underline-offset-2 hover:text-red-700 dark:hover:text-red-300"
      >
        Supprimer mon compte
      </button>
    </div>
  );
}

export default function HomePage() {
  const { inviteCode: routeInviteCode } = useParams();
  const { token, user } = useAuth();
  const { family, members, refresh, isLoading: familyLoading } = useFamily();
  const { embedded, navigateEmbedded } = useEmbeddedNavigation();
  const [activeProfileSection, setActiveProfileSection] = useState(null);
  const [copied, setCopied] = useState(false);
  const questionnaireReadyRef = useRef(null);
  const wasQuestionnaireReadyRef = useRef(false);

  const myMember = useMemo(
    () => (members || []).find((m) => m.uuid === user?.uuid) || null,
    [members, user?.uuid]
  );

  const displayName = myMember?.name || user?.name || user?.email || 'Utilisateur';

  useAnalytics({
    currentMember: displayName,
    currentFamily: family
  });

  const {
    isLoading: onboardingLoading,
    error: onboardingError,
    profileComplete,
    hasStartedQuestionnaire,
    hasCompletedCategory,
    allCategoriesCompleted,
    overallPercent,
    nextCategoryId
  } = useOnboardingStatus({
    token,
    family,
    member: myMember
  });

  const inviteCode = family?.invite_code || '';
  const questionnairePath = nextCategoryId ? `/questionnaire/${nextCategoryId}` : '/questionnaire';
  const isQuestionnaireReadyScreen = Boolean(family && myMember && profileComplete && !hasCompletedCategory);
  const profileSheetTitle = useMemo(() => {
    if (activeProfileSection === 'all') return 'Mes informations';
    if (activeProfileSection === PROFILE_FORM_SECTIONS.personal) return 'Vous';
    if (activeProfileSection === PROFILE_FORM_SECTIONS.household) return 'Foyer';
    if (activeProfileSection === PROFILE_FORM_SECTIONS.statistics) return 'Statistiques';
    return 'Mes informations';
  }, [activeProfileSection]);
  const profileSheetSections = useMemo(() => {
    if (!activeProfileSection || activeProfileSection === 'all') {
      return [
        PROFILE_FORM_SECTIONS.household,
        PROFILE_FORM_SECTIONS.personal,
        PROFILE_FORM_SECTIONS.statistics
      ];
    }

    return [activeProfileSection];
  }, [activeProfileSection]);

  const canShare = useMemo(() => typeof navigator !== 'undefined' && typeof navigator.share === 'function', []);
  const personalInfoItems = useMemo(() => ([
    { label: 'Travail/sem', value: formatDecimalHoursCompact(myMember?.work_hours_per_week) },
    { label: 'Sommeil/nuit', value: formatDecimalHoursCompact(myMember?.sleep_hours_per_night) },
    { label: 'Transport/sem', value: formatDecimalHoursCompact(myMember?.commute_hours_per_week) },
    { label: 'Absences/an', value: myMember?.away_weeks_per_year ? `${myMember.away_weeks_per_year} sem` : '-' }
  ]), [myMember]);
  const householdInfoItems = useMemo(() => {
    const sharedCustodyValue = myMember?.children_shared_custody_count > 0
      ? `${myMember.children_shared_custody_count} (${myMember.shared_custody_home_percent || 0}%)`
      : 'Aucune';

    return [
      { label: 'Votre Foyer', value: family?.name || '-' },
      { label: 'Membres', value: myMember?.household_size || '-' },
      { label: 'Enfants', value: myMember?.children_full_time_count ?? 0 },
      { label: 'Garde partagée', value: sharedCustodyValue }
    ];
  }, [family?.name, myMember]);
  const statisticsInfoItems = useMemo(() => {
    const genderLabelMap = {
      male: 'Homme',
      female: 'Femme',
      other: 'Autre'
    };

    return [
      { label: 'Département', value: myMember?.department || 'Non renseigné' },
      { label: 'Naissance', value: myMember?.birth_year || 'Non renseigné' },
      { label: 'Genre', value: genderLabelMap[myMember?.gender] || 'Non renseigné' },
      { label: 'Revenu', value: myMember?.annual_income_bracket || 'Non renseigné' }
    ];
  }, [myMember]);

  const onCopy = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      // Fallback
      try {
        const el = document.createElement('textarea');
        el.value = inviteCode;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      } catch (err) {}
    }
  };

  const onShare = async () => {
    if (!inviteCode || !canShare) return;
    try {
      await navigator.share({
        title: 'Paye ta charge mentale !',
        text: `Rejoins-moi pour comparer ta charge mentale avec la mienne\n\nhttps://payetachargementale.com/join/${inviteCode}`
      });
    } catch (e) {
      // user cancelled -> ignore
    }
  };
  const scrollToInviteSection = () => {
    if (typeof document === 'undefined') return;
    document.getElementById('home-invite-code-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const openProfileSheet = (section = 'all') => {
    setActiveProfileSection(section);
  };
  const closeProfileSheet = () => {
    setActiveProfileSection(null);
  };

  useEffect(() => {
    const shouldScrollToTop = isQuestionnaireReadyScreen && !wasQuestionnaireReadyRef.current;
    wasQuestionnaireReadyRef.current = isQuestionnaireReadyScreen;

    if (!shouldScrollToTop) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      scrollElementToTop(questionnaireReadyRef.current);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isQuestionnaireReadyScreen]);

  if (!family && !familyLoading) {
    if (routeInviteCode) {
      if (embedded) {
        return (
          <div className="min-h-full flex items-center justify-center px-3 sm:px-4 py-6">
            <div className="w-full">
              <Onboarding initialInviteCode={routeInviteCode} embedded />
            </div>
          </div>
        );
      }

      return <Onboarding initialInviteCode={routeInviteCode} embedded={embedded} />;
    }

    if (embedded) {
      return (
        <div className="min-h-full flex items-center justify-center px-3 sm:px-4 py-6">
          <div className="w-full">
            <Onboarding embedded />
          </div>
        </div>
      );
    }

    return <LoggedOutLandingPage />;
  }

  if (familyLoading || (family && !myMember) || (family && myMember && profileComplete && onboardingLoading)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl font-semibold text-ptcm-muted">Chargement...</div>
      </div>
    );
  }

  if (!profileComplete) {
    return (
      <ProfileSetupFlow
        embedded={embedded}
        token={token}
        member={myMember}
        onCompleted={async () => {
          await refresh();
        }}
      />
    );
  }

  if (!hasCompletedCategory) {
    return (
      <>
        <Sheet
          open={Boolean(activeProfileSection)}
          onClose={closeProfileSheet}
          title={profileSheetTitle}
          mobileFullscreen
        >
          <HouseholdProfileForm
            token={token}
            member={myMember}
            onSaved={async () => {
              await refresh();
              closeProfileSheet();
            }}
            embedded
            visibleSections={profileSheetSections}
            submitLabel="Enregistrer"
          />
        </Sheet>

        <div
          ref={questionnaireReadyRef}
          className={embedded ? 'max-w-3xl mx-auto px-3 sm:px-4 py-4 space-y-4' : 'max-w-3xl mx-auto mt-6 px-3 sm:px-4 pb-10 space-y-4'}
        >
          {onboardingError ? (
            <div className="rounded-[24px] border border-red-200/70 dark:border-red-900/30 bg-red-50/90 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              {onboardingError}
            </div>
          ) : null}

          <Card>
            <CardHeader className="space-y-4">
              <div>
                <CardTitle className="text-2xl leading-tight">
                  {hasStartedQuestionnaire ? 'Reprenez votre premier thème' : 'Votre questionnaire est prêt !'}
                </CardTitle>
              </div>
            </CardHeader>

            <div className="p-6">
              <QuestionnaireInfoStep stepId={QUESTIONNAIRE_INFO_STEP_IDS.questionnaire} />
            </div>

            <CardContent className="space-y-5">
              {hasStartedQuestionnaire ? (
                <div className="rounded-[24px] border border-amber-200/70 dark:border-amber-900/30 bg-amber-50/90 dark:bg-amber-900/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                  Progression actuelle : {overallPercent}% du questionnaire répondu.
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="sm:flex-1" onClick={() => navigateEmbedded(questionnairePath)}>
                  {hasStartedQuestionnaire ? 'Continuer le questionnaire' : 'Commencer le questionnaire'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <DeleteAccountLink />
        </div>
      </>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-6 px-3 sm:px-4 space-y-4">
      <Sheet
        open={Boolean(activeProfileSection)}
        onClose={closeProfileSheet}
        title={profileSheetTitle}
        mobileFullscreen
      >
        <HouseholdProfileForm
          token={token}
          member={myMember}
          onSaved={async () => {
            await refresh();
            closeProfileSheet();
          }}
          embedded
          visibleSections={profileSheetSections}
          submitLabel="Enregistrer"
        />
      </Sheet>

      {onboardingError ? (
        <div className="rounded-[24px] border border-red-200/70 dark:border-red-900/30 bg-red-50/90 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {onboardingError}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        <CardHeader className="p-4 pb-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-lg">
                {allCategoriesCompleted ? 'Questionnaire terminé !' : `Continuez votre questionnaire`}
              </CardTitle>
              <CardDescription className="mt-1 text-sm">
                {allCategoriesCompleted
                  ? (
                    members.length === 1
                      ? 'Vos stats sont prêtes. Invitez votre conjoint·e si besoin.'
                      : 'Vos stats sont prêtes. Comparez-les avec votre conjoint·e.'
                  )
                  : `${overallPercent}% complété. Les premiers résultats sont déjà disponibles.`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-3 flex flex-col sm:flex-row gap-2">
          <Button
            size="md"
            variant={allCategoriesCompleted ? 'outline' : 'default'}
            onClick={() => {
              if (!allCategoriesCompleted) {
                navigateEmbedded(questionnairePath);
                return;
              }

              if (members.length === 1) {
                scrollToInviteSection();
                return;
              }

              navigateEmbedded(questionnairePath);
            }}
          >
            {allCategoriesCompleted
              ? (members.length === 1 ? 'Partager avec mon/ma conjoint·e' : 'Revoir le questionnaire')
              : 'Continuer le questionnaire'}
          </Button>
        </CardContent>
      </Card>

      {members.length === 1 && (
      <Card className="overflow-hidden">
        <CardContent className="p-4 pt-3 space-y-3">
          <div
            id="home-invite-code-card"
            className="rounded-[26px] border border-ptcm-border dark:border-blue-900/30 bg-[linear-gradient(135deg,rgba(213,238,225,0.72)_0%,rgba(247,222,227,0.72)_100%)] dark:bg-blue-900/10 p-4 space-y-3"
          >
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 text-ptcm-ink dark:text-blue-300">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" x2="12" y1="2" y2="15" />
                </svg>
                <h3 className="font-semibold text-sm">Inviter votre partenaire</h3>
              </div>
              <p className="text-xs text-ptcm-muted dark:text-blue-400/80">
                Partagez ce code ou le lien d'invitation.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-2xl bg-white dark:bg-gray-950 border border-ptcm-border dark:border-blue-800 px-3 py-3 shadow-ptcm-float flex items-center justify-center min-w-0">
                  <code className="font-mono text-base font-bold tracking-[0.25em] text-ptcm-ink dark:text-gray-100 truncate">
                    {inviteCode}
                  </code>
                </div>
                <Button
                  variant="outline"
                  size="md"
                  onClick={onCopy}
                  disabled={!inviteCode}
                  className="shrink-0"
                >
                  {copied ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Copié
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                        <path d="M4 16c-1.1 0-2-.9-2 0V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                      Copier
                    </>
                  )}
                </Button>
              </div>
              {canShare ? (
                <Button
                  variant="default"
                  size="md"
                  onClick={onShare}
                  disabled={!inviteCode}
                  className="w-full"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" x2="12" y1="2" y2="15" />
                  </svg>
                  Partager
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      <ProfileInfoSectionCard
        title="Vous"
        description="Votre rythme de vie personnel"
        items={personalInfoItems}
        onEdit={() => openProfileSheet(PROFILE_FORM_SECTIONS.personal)}
      />
      <ProfileInfoSectionCard
        title="Votre foyer"
        description="La composition et le contexte du foyer"
        items={householdInfoItems}
        onEdit={() => openProfileSheet(PROFILE_FORM_SECTIONS.household)}
      />
      <ProfileInfoSectionCard
        title="Statistiques"
        description="Ces informations nous permettent de compiler des statistiques nationales"
        items={statisticsInfoItems}
        onEdit={() => openProfileSheet(PROFILE_FORM_SECTIONS.statistics)}
      />

      <DeleteAccountLink />
    </div>
  );
}
